import { authentication } from '@microsoft/teams-js'
import { getCachedM365Environment } from '@helpers/environmentHelpers'

let cachedToken: { value: string; expiresAt: number } | null = null
let tokenRequest: Promise<string | null> | null = null

export async function getTeamsSsoToken(silent = true, throwOnError = false): Promise<string | null> {
    if (getCachedM365Environment().environment !== 'Teams') return null

    if (cachedToken && cachedToken.expiresAt > Date.now() + 60_000) {
        return cachedToken.value
    }

    // A cold Teams deep link can cause the feed, highlighted post, and its
    // embedded images to request a token at the same time. Teams SSO does not
    // reliably handle those overlapping host calls, so share the in-flight
    // request (and its result) across all API requests.
    if (tokenRequest) return tokenRequest

    tokenRequest = requestTeamsSsoToken(silent, throwOnError)
    try {
        return await tokenRequest
    } finally {
        tokenRequest = null
    }
}

async function requestTeamsSsoToken(silent: boolean, throwOnError: boolean): Promise<string | null> {
    try {
        const token = await authentication.getAuthToken({ silent })
        cachedToken = { value: token, expiresAt: getTokenExpiry(token) }
        return token
    } catch (error) {
        console.error('Teams SSO token acquisition failed', error)
        if (throwOnError) throw error
        return null
    }
}

function getTokenExpiry(token: string) {
    try {
        const payload = token.split('.')[1]
        if (!payload) return Date.now() + 5 * 60_000
        const normalized = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '=')
        const decoded = JSON.parse(atob(normalized)) as { exp?: number }
        return typeof decoded.exp === 'number' ? decoded.exp * 1000 : Date.now() + 5 * 60_000
    } catch {
        return Date.now() + 5 * 60_000
    }
}
